import {
  buscarFechaCaducidad,
  acumularProducto,
  registrarClave,
  esTokenNumerico,
  aNumero,
  cuadraImporte,
} from "./comun";

/**
 * Parsea las líneas de texto extraídas de un PDF de SALUBOX SAPI DE CV (RFC: SAL1508035B3).
 * Retorna un Map<clave, { lotes, cantidadLinea }>.
 *
 * Maqueta de la fila de producto:
 *   CLAVE SAT | UNIDAD SAT | LOTE | FECHA CAD. | ALMACÉN | DESCRIPCIÓN | CANTIDAD |
 *   P. UNITARIO | IMPORTE
 *
 * Ejemplo real (factura GDL4060451):
 *   51201625 H87 PZA 5DD61D3 30-Apr-2028 GDL STAMARIL 4 2,370.00 9,480.00
 *
 * Se ancla en la FECHA, no en el orden de columnas: es el único token inequívoco de la fila.
 * El lote es el token pegado a su izquierda y la cabecera debe abrir con la clave SAT de 8
 * dígitos, que es la firma del formato. Lo que sigue a la fecha se analiza por la cola.
 *
 * Ojo con la fecha: Salubox la imprime en INGLÉS (30-Apr-2028, 27-Jan-2028) y la app corre
 * con moment.locale("es"), así que el token MMM de moment no la parsea. De eso se encarga
 * parsearFechaCaducidad, con mapa de meses explícito.
 */

const CLAVE_SAT = /^\d{8}$/;
// Código de almacén pegado antes de la descripción ("GDL")
const ALMACEN = /^[A-Z]{2,5}$/;

/**
 * De lo que sigue a la fecha saca descripción y cantidad.
 * La cola es "ALMACEN DESCRIPCION CANTIDAD PRECIO IMPORTE"; la cantidad se valida con
 * cantidad x precio ~= importe y, si no aparece, se deduce de importe / precio.
 */
function analizarCola(resto) {
  const tokens = resto.split(" ").filter(Boolean);
  if (tokens.length === 0) return null;

  const numericos = [];
  tokens.forEach((token, indice) => {
    if (esTokenNumerico(token)) numericos.push({ indice, valor: aNumero(token) });
  });

  // Sin precio e importe no hay cola que analizar: la descripción es todo y la cantidad
  // queda pendiente de que la teclee el usuario.
  if (numericos.length < 2 || numericos[numericos.length - 1].indice !== tokens.length - 1) {
    return { descripcion: tokens.join(" "), cantidad: null };
  }

  const importe = numericos[numericos.length - 1];
  const precio = numericos[numericos.length - 2];

  let cantidad = null;
  let indiceCorte = precio.indice;
  for (let i = numericos.length - 3; i >= 0; i--) {
    if (cuadraImporte(numericos[i].valor, precio.valor, importe.valor)) {
      cantidad = numericos[i].valor;
      indiceCorte = numericos[i].indice;
      break;
    }
  }

  // La cantidad quedó en otra fila visual: se deduce de la aritmética del renglón
  if (cantidad === null && precio.valor > 0) {
    const deducida = importe.valor / precio.valor;
    if (deducida > 0) cantidad = Math.round(deducida * 1000) / 1000;
  }

  return { descripcion: tokens.slice(0, indiceCorte).join(" ").trim(), cantidad };
}

export function parsearLineasSalubox(allLines) {
  const productsByDesc = new Map();

  for (const rawLine of allLines || []) {
    const linea = String(rawLine ?? "").replace(/\s+/g, " ").trim();
    if (!linea) continue;

    const fecha = buscarFechaCaducidad(linea);
    if (!fecha) continue;

    const tokensAntes = linea.slice(0, fecha.indice).split(" ").filter(Boolean);
    if (tokensAntes.length < 2) continue;
    if (!CLAVE_SAT.test(tokensAntes[0])) continue;

    const lote = tokensAntes[tokensAntes.length - 1];
    const cola = analizarCola(linea.slice(fecha.indice + fecha.texto.length).trim());
    if (!cola || !cola.descripcion) continue;

    const entrada = acumularProducto(productsByDesc, {
      descripcion: cola.descripcion,
      lote,
      caducidad: fecha.fecha,
      cantidad: cola.cantidad,
    });

    // La descripción puede venir precedida del código de almacén ("GDL STAMARIL"). Se
    // registra también sin él, como hace Fanasa.js:60-67, para no perder el empate.
    const tokens = cola.descripcion.split(" ");
    if (entrada && tokens.length > 1 && ALMACEN.test(tokens[0])) {
      registrarClave(productsByDesc, tokens.slice(1).join(" "), entrada);
    }
  }

  return productsByDesc;
}

export default parsearLineasSalubox;
